import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Guid } from '@models/guid';
import { Prompt } from '@models/prompt.model';
import { ThirdPartyId } from '@models/third-party-id.enum';
import { SmartFeatureSdk } from '@sdks/smart-feature.sdk';
import { DocViewerStore } from '@store/legacy/doc-viewer.store';
import { ComparisonUtility } from '@utilities/comparison.utility';
import { BehaviorSubject, EMPTY, Observable, catchError, distinctUntilChanged, map, of, switchMap, takeUntil, tap, timer } from 'rxjs';
import { BaseComponent } from 'src/app/components/base.component';

@Component({
	selector: 'lex-doc-viewer-summary',
	templateUrl: './doc-viewer-summary.component.html',
	styleUrl: './doc-viewer-summary.component.scss',
	standalone: false
})
export class DocViewerSummaryComponent extends BaseComponent implements OnInit, OnDestroy {

	@Input() set summaryPromptId(value: string) { this.summaryPromptId$.next(value); }
	@Input() set summaryPrompts(value: Prompt[]) { this.summaryPrompts$.next(value); }

	public summary = '';
	public isLoading = false;

	private currentDocId: string = '';
	private currentWorkflowKey: string = '';
	private currentSummaryPromptId: string = '';
	private currentSummaryPrompts: Prompt[] = [];
	private initialChanged: boolean = false;

	private isReviewMode: boolean = false;

	private summaryPromptId$ = new BehaviorSubject<string>('');
	private summaryPrompts$ = new BehaviorSubject<Prompt[]>([]);

	constructor(
		private docViewerStore: DocViewerStore,
		private smartFeatureSdk: SmartFeatureSdk
	) {
		super();
		this.summaryPromptId$.pipe(
			distinctUntilChanged((a, b) => a === b),
			map((promptId) => this.currentSummaryPromptId = promptId),
			switchMap(() => {
				if (this.initialChanged)
					return this.getSummary()
				return EMPTY;
			}),
			takeUntil(this.destroy$)
		).subscribe();

		this.summaryPrompts$.pipe(
			distinctUntilChanged((a, b) => ComparisonUtility.isEqual(a, b)),
			map((prompts) => this.currentSummaryPrompts = prompts),
			switchMap(() => {
				if (this.initialChanged)
					return this.getSummary()
				return EMPTY;
			}),
			takeUntil(this.destroy$)
		).subscribe();
	}

	ngOnInit(): void {
		this.docViewerStore.simpleState$.pipe(
			tap((state) => {
				this.isReviewMode = state.mode === 'review';
			}),
			takeUntil(this.destroy$)
		).subscribe();

		this.docViewerStore.currentDocument$.pipe(
			distinctUntilChanged((a, b) => a.docId === b.docId),
			map((doc) => this.currentDocId = doc.docId),
			switchMap(() => {
				if (this.isLoading) {
					this.currentWorkflowKey = null;
					this.summary = '';
					this.isLoading = false;
				}
				return this.getSummary()
			}),
			switchMap(() => this.fetchNextSummary(this.isReviewMode)),
			takeUntil(this.destroy$),
		).subscribe();
	}

	private fetchNextSummary(isReviewMode: boolean): Observable<void> {
		if (isReviewMode) {
			return this.docViewerStore.simpleState$.pipe(
				map((state) => state.reviewDocs),
				switchMap((reviewDocs) => {
					const reviewDoc = reviewDocs.find(x => x.docId === this.currentDocId);
					if (reviewDoc) {
						const nextReviewDoc = reviewDocs.find(x => x.reviewOrder === reviewDoc.reviewOrder + 1);
						if (nextReviewDoc)
							return this.smartFeatureSdk.fetchSummary({
								externalAppId: ThirdPartyId.OpenAi,
								docId: nextReviewDoc.docId,
								promptId: this.currentSummaryPromptId
							});
						else
							return this.smartFeatureSdk.fetchSummary({
								externalAppId: ThirdPartyId.OpenAi,
								reviewSetId: reviewDoc.reviewSetId,
								reviewOrder: reviewDoc.reviewOrder + 1,
								promptId: this.currentSummaryPromptId
							});
					}
					return of(null);
				})
			)
		} else {
			return this.docViewerStore.docList$.pipe(
				switchMap((docList) => {
					const index = docList.findIndex(x => x.docId === this.currentDocId);
					if (index > -1 && index <= docList.length - 2) {
						return this.smartFeatureSdk.fetchSummary({
							externalAppId: ThirdPartyId.OpenAi,
							docId: docList[index + 1].docId,
							promptId: this.currentSummaryPromptId
						});
					}
					return of(null);
				})
			);
		}
	}

	private getSummary(forceRefresh: boolean = false): Observable<string | void> {
		if (!forceRefresh && this.isLoading)
			return EMPTY;

		this.initialChanged = true;
		this.isLoading = true;
		this.currentWorkflowKey = null;
		this.summary = '';
		if (this.currentSummaryPromptId) {
			return this.smartFeatureSdk.getSummary({
				externalAppId: ThirdPartyId.OpenAi,
				docId: this.currentDocId,
				promptId: this.currentSummaryPromptId
			}).pipe(switchMap((data) => {
				if (data.summary) {
					this.isLoading = false;
					this.summary = data.summary;
				}
				else if (data) {
					this.currentWorkflowKey = data;
					this.scheduleStreaming(3000);
				}
				return of(null);
			}), catchError((error: HttpErrorResponse) => {
				if (error.status === 415) {
					this.isLoading = false;
					this.summary = 'The document is not currently supported, but we are hard at work making this possible.';
				}
				return of(null);
			}))
		}
		this.displayHelpMessage();
		return EMPTY;
	}

	private displayHelpMessage(): void {
		if (this.currentSummaryPromptId)
			return;

		if (this.currentSummaryPrompts.length > 1) {
			this.isLoading = false;
			this.summary = 'Multiple summary prompts are available. Please choose one to proceed.';
		}
		else if (this.currentSummaryPrompts.length === 0) {
			this.isLoading = false;
			this.summary = 'The summary prompt has not been setup. Please contact Professional Service for support.';
		}
	}

	private scheduleStreaming(waitInMs: number): void {
		timer(waitInMs).pipe(
			switchMap(() => {
				if (!this.currentWorkflowKey) return EMPTY;
				return this.smartFeatureSdk.getResponse(this.currentWorkflowKey);
			}),
			map((response) => {
				if (!response) {
					this.scheduleStreaming(3000);
					return;
				}

				if (response.aiMetadataId === Guid.Empty) {
					if (response.result.length === 110) {
						this.currentWorkflowKey = response.result;
						this.scheduleStreaming(0);
					}
					else {
						this.getSummary(true).subscribe();
					}
					return;
				}

				this.summary = response.result;
				if (response.isError)
					this.summary = response.result ?? "An error has occurred. Please try again after a few minutes, or contact Professional Services for more support.";
				if (response.completed)
					this.isLoading = false;
				if (!response.isError && !response.completed)
					this.scheduleStreaming(1000);
			})
		).subscribe();
	}
}
